import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faLock,
  faShieldHalved,
  faPaperPlane,
  faChartLine,
} from '@fortawesome/free-solid-svg-icons';
import { FaFacebookF } from 'react-icons/fa';
import { loginUser } from '../lib/api';
import { setLoginEmailForSession } from '../lib/adminEmail';
import { SUPPORT_EMAIL } from '../lib/siteContact';
import AppLogo from '../components/AppLogo';
import SiteFooter from '../components/SiteFooter';
import './login.css';

const FB_SDK_VERSION = 'v21.0';
const BIZ_APP_ONBOARDING_EVENT = 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING';
const FINISH_EVENTS = new Set([
  'FINISH',
  'FINISH_ONLY_WABA',
  BIZ_APP_ONBOARDING_EVENT,
  'FINISH_OBO_MIGRATION',
  'FINISH_GRANT_ONLY_API_ACCESS',
]);

/**
 * Embedded Signup v4 (Meta): products come from the Facebook Login for Business
 * configuration (config_id). featureType enables WhatsApp Business app
 * coexistence (same number on the Business app and Cloud API). sessionInfoVersion
 * must stay "3" — v2 ("2") is deprecated. solutionID stays in setup only for
 * Multi-Partner Solutions.
 * @see https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/onboarding-business-app-users
 */
const buildEmbeddedSignupExtras = (solutionId) => ({
  featureType: 'whatsapp_business_app_onboarding',
  sessionInfoVersion: '3',
  setup: solutionId ? { solutionID: solutionId } : {},
});

const isFacebookOrigin = (origin) => {
  try {
    const host = new URL(origin).hostname;
    return host === 'facebook.com' || host.endsWith('.facebook.com');
  } catch {
    return false;
  }
};

const waitForSignupSession = (sessionRef, timeoutMs = 2000) =>
  new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const session = sessionRef.current;
      const eventName = session?.event;
      const settled =
        FINISH_EVENTS.has(eventName) || eventName === 'CANCEL' || eventName === 'ERROR';
      if (settled || Date.now() - started >= timeoutMs) {
        resolve(session || null);
        return;
      }
      window.setTimeout(tick, 50);
    };
    tick();
  });

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fbStatus, setFbStatus] = useState('');
  const [fbTone, setFbTone] = useState('info');
  const [fbReady, setFbReady] = useState(false);
  const [fbLoading, setFbLoading] = useState(false);
  const signupSessionRef = useRef(null);

  const showFbStatus = (message, tone = 'info') => {
    setFbStatus(message);
    setFbTone(tone);
  };

  const appId = process.env.REACT_APP_FACEBOOK_APP_ID || '981071426908731';
  // Must be a v4 Facebook Login for Business configuration ID (products selected in App Dashboard).
  const configId = process.env.REACT_APP_FACEBOOK_CONFIG_ID || '1076964211751146';
  const solutionId = process.env.REACT_APP_FACEBOOK_SOLUTION_ID || '';

  useEffect(() => {
    let cancelled = false;

    const markReady = () => {
      if (!cancelled) setFbReady(true);
    };

    const initFacebookSdk = () => {
      if (cancelled || !window.FB) return;
      window.FB.init({
        appId,
        cookie: true,
        xfbml: true,
        version: FB_SDK_VERSION,
      });
      markReady();
    };

    window.fbAsyncInit = initFacebookSdk;

    // Session logging for Embedded Signup (v3+/v4 send WA_EMBEDDED_SIGNUP events).
    const onEmbeddedSignupMessage = (event) => {
      if (!isFacebookOrigin(event?.origin)) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'WA_EMBEDDED_SIGNUP') {
          signupSessionRef.current = data;
          console.log('[embedded-signup] session event:', data.event);
        }
      } catch {
        // Ignore non-JSON postMessages from other Facebook frames.
      }
    };
    window.addEventListener('message', onEmbeddedSignupMessage);

    if (window.FB) {
      initFacebookSdk();
      return () => {
        cancelled = true;
        window.removeEventListener('message', onEmbeddedSignupMessage);
      };
    }

    let script = document.getElementById('facebook-jssdk');
    if (!script) {
      script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.async = true;
      script.defer = true;
      script.src = `https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=${FB_SDK_VERSION}&appId=${appId}`;
      document.body.appendChild(script);
    }

    const onScriptLoad = () => {
      initFacebookSdk();
    };

    script.addEventListener('load', onScriptLoad);

    if (window.FB) {
      initFacebookSdk();
    } else if (script.readyState === 'complete' || script.readyState === 'loaded') {
      const pollId = window.setInterval(() => {
        if (window.FB) {
          window.clearInterval(pollId);
          initFacebookSdk();
        }
      }, 100);
      window.setTimeout(() => window.clearInterval(pollId), 10000);
      return () => {
        cancelled = true;
        window.clearInterval(pollId);
        script.removeEventListener('load', onScriptLoad);
        window.removeEventListener('message', onEmbeddedSignupMessage);
      };
    }

    return () => {
      cancelled = true;
      script.removeEventListener('load', onScriptLoad);
      window.removeEventListener('message', onEmbeddedSignupMessage);
    };
  }, [appId]);

  const handleFacebookLogin = () => {
    setFbStatus('');
    if (!fbReady || !window.FB) {
      showFbStatus('Facebook SDK is still loading. Please wait a moment and try again.', 'error');
      return;
    }

    signupSessionRef.current = null;
    setFbLoading(true);
    window.FB.login(
      function (response) {
        const authCode = response.authResponse?.code;
        if (response.status !== 'connected' || !authCode) {
          setFbLoading(false);
          const session = signupSessionRef.current;
          const errorMessage = session?.data?.error_message;
          if (errorMessage) {
            showFbStatus(errorMessage, 'error');
            return;
          }
          showFbStatus('Facebook login was not successful. Please try again.', 'error');
          return;
        }

        showFbStatus('Processing Facebook login...');
        waitForSignupSession(signupSessionRef)
          .then((session) => {
            const eventName = session?.event;
            const coexistence = eventName === BIZ_APP_ONBOARDING_EVENT;
            const assetIds = session?.data || {};
            return fetch(`${process.env.REACT_APP_API_BASE_URL}/embedded/exchange-code`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                code: authCode,
                solution_id: solutionId || undefined,
                event: eventName,
                waba_id: assetIds.waba_id,
                phone_number_id: assetIds.phone_number_id,
                business_id: assetIds.business_id,
                // Already registered on the WhatsApp Business app — do not call register.
                coexistence,
                skip_phone_registration: coexistence,
              }),
            }).then(async (res) => {
              const body = await res.json().catch(() => ({}));
              if (res.status === 200) return { body, coexistence, eventName };
              throw new Error(body?.error || 'Failed to process login. Please try again.');
            });
          })
          .then(({ body, coexistence, eventName }) => {
            const verifiedName = body.verified_name;
            if (coexistence) {
              showFbStatus(
                verifiedName
                  ? `${verifiedName}, your WhatsApp Business app is connected. We are synchronizing your contacts and recent chats. Keep the WhatsApp Business app open — this can take several minutes.`
                  : 'Your WhatsApp Business app is connected. We are synchronizing your contacts and recent chats. Keep the WhatsApp Business app open — this can take several minutes.',
                'success'
              );
              return;
            }
            if (verifiedName) {
              showFbStatus(
                `Welcome, ${verifiedName}! Registration successful. Verification in progress.`,
                'success'
              );
              return;
            }
            if (eventName === 'FINISH_ONLY_WABA') {
              showFbStatus(
                'Registration received. If a phone number was not added yet, finish setup in Meta Business Suite, then try again.'
              );
              return;
            }
            showFbStatus(
              'Registration received. If a phone number was not added yet, finish setup in Meta Business Suite, then try again.'
            );
          })
          .catch((err) => {
            showFbStatus(err?.message || 'An error occurred during Facebook login.', 'error');
          })
          .finally(() => {
            setFbLoading(false);
          });
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: buildEmbeddedSignupExtras(solutionId),
      }
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await loginUser({ email, password });
      jwtDecode(data.token);
      setLoginEmailForSession(email);
      navigate('/main');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="login">
      <div className="login__backdrop login__backdrop--brand" aria-hidden />
      <div className="login__backdrop login__backdrop--form" aria-hidden />

      <aside className="login__brand">
        <AppLogo to="/" variant="brand" />

        <div className="login__meta-badge">
          <img
            src={`${process.env.PUBLIC_URL}/meta.jpeg`}
            alt="Meta Business Partner"
            className="login__meta-badge__img"
          />
        </div>

        <h1 className="login__title">
          Sign in to your <span>WhatsApp</span> dashboard
        </h1>
        <p className="login__lead">
          Manage campaigns, templates, reports, and customer conversations from one secure business
          portal.
        </p>

        <ul className="login__features">
          <li>
            <FontAwesomeIcon icon={faPaperPlane} />
            Send and track business messages
          </li>
          <li>
            <FontAwesomeIcon icon={faChartLine} />
            Monitor delivery and engagement
          </li>
          <li>
            <FontAwesomeIcon icon={faShieldHalved} />
            Secure, Meta-approved platform
          </li>
        </ul>
      </aside>

      <main className="login__form-area">
        <div className="login__card">
          <div className="login__card-head">
            <h2>Welcome back</h2>
            <p>Sign in with your business account credentials.</p>
          </div>

          <form onSubmit={onSubmit} className="login__form">
            <div className="login__field">
              <label htmlFor="email">Email address</label>
              <div className="login__input-wrap">
                <FontAwesomeIcon icon={faEnvelope} className="login__input-icon" />
                <input
                  id="email"
                  type="email"
                  className="login__input"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="login__field">
              <div className="login__field-head">
                <label htmlFor="password">Password</label>
                <Link to="/forgot-password" className="login__forgot-link">
                  Forgot password?
                </Link>
              </div>
              <div className="login__input-wrap">
                <FontAwesomeIcon icon={faLock} className="login__input-icon" />
                <input
                  id="password"
                  type="password"
                  className="login__input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="login__alert login__alert--error" role="alert">
                {error}
              </div>
            )}

            <button className="login__btn login__btn--primary" type="submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="login__divider">
            <span>or</span>
          </div>

          <button
            type="button"
            className="login__btn login__btn--facebook"
            onClick={handleFacebookLogin}
            disabled={!fbReady || fbLoading}
          >
            <FaFacebookF aria-hidden />
            {fbLoading
              ? 'Connecting…'
              : fbReady
                ? 'Connect WhatsApp Business'
                : 'Loading Facebook SDK…'}
          </button>

          <p className="login__fineprint">
            Already using the WhatsApp Business app? Connect that same number here. After you finish,
            keep the app open while contacts and recent chats synchronize.
          </p>

          {fbStatus && (
            <div className={`login__alert login__alert--${fbTone}`} role="status">
              {fbStatus}
            </div>
          )}

          <p className="login__fineprint">
            By signing in, you agree to our{' '}
            <Link to="/terms-of-service">Terms of Service</Link> and{' '}
            <Link to="/privacy-policy">Privacy Policy</Link>.
          </p>

          <div className="login__help">
            <span>Need help?</span>
            <a href={`mailto:${SUPPORT_EMAIL}`}>Contact support</a>
            <span className="login__help-sep">·</span>
            <Link to="/">Back to website</Link>
          </div>
        </div>

        <p className="login__powered">Powered by Innovative Info Solutions</p>
      </main>
      </div>
      <SiteFooter />
    </div>
  );
};

export default Login;
